import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import {
  BedDouble,
  CalendarDays,
  Check,
  ClipboardList,
  Loader2,
  Plane,
  PlaneLanding,
  PlaneTakeoff,
  Send,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  arrival_date: "",
  departure_date: "",
  arrival_flight: "",
  departure_flight: "",
  party_size: "2",
  already_booked: false,
  hotel_name: "",
  hotel_address: "",
  existing_bookings: "",
  message: "",
};

export const ConciergeDrawer = ({ open, onOpen, onClose, selectedServices, onToggle, onClear, country = "Iceland" }) => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [confirmedId, setConfirmedId] = useState(null);

  const set = (key) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can reach you.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/requests`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        arrival_date: form.arrival_date,
        departure_date: form.departure_date,
        arrival_flight: form.arrival_flight,
        departure_flight: form.departure_flight,
        party_size: form.party_size,
        country,
        services: selectedServices.map((s) => s.name),
        accommodation: {
          already_booked: form.already_booked,
          hotel_name: form.hotel_name,
          hotel_address: form.hotel_address,
          existing_bookings: form.existing_bookings,
        },
        message: form.message,
      });
      setConfirmedId(res.data.id);
      onClear();
      toast.success("Request received — our concierge will reply within hours.");
    } catch (e) {
      toast.error("Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const closeAndReset = () => {
    onClose();
    if (confirmedId) {
      setTimeout(() => {
        setConfirmedId(null);
        setForm(initialForm);
      }, 400);
    }
  };

  return (
    <>
      <motion.button
        data-testid="open-trip-request-drawer"
        onClick={onOpen}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-full border border-amber-400/50 bg-black/80 py-3 pl-5 pr-4 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 shadow-[0_8px_32px_rgba(0,0,0,0.8)] backdrop-blur-xl transition-colors duration-300 hover:bg-amber-500 hover:text-black"
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
      >
        <ClipboardList className="h-4 w-4" />
        Your List
        <motion.span
          key={selectedServices.length}
          initial={{ scale: 0.4 }}
          animate={{ scale: 1 }}
          className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-black"
        >
          {selectedServices.length}
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="drawer-backdrop"
              className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeAndReset}
            />
            <motion.aside
              data-testid="concierge-drawer"
              className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-lg flex-col border-l border-amber-500/25 bg-[#0c0c10]"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 200, damping: 28 }}
            >
              <div className="flex items-center justify-between border-b border-amber-500/15 px-6 py-5">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Private Request</p>
                  <h3 className="mt-1 font-luxe text-lg uppercase tracking-[0.15em] text-neutral-50">Your Concierge List</h3>
                </div>
                <button
                  data-testid="drawer-close-button"
                  onClick={closeAndReset}
                  className="flex h-10 w-10 items-center justify-center border border-neutral-700 text-neutral-400 transition-colors hover:border-amber-400 hover:text-amber-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-6">
                {confirmedId ? (
                  <motion.div
                    data-testid="request-success"
                    className="flex h-full flex-col items-center justify-center text-center"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <span className="flex h-20 w-20 items-center justify-center rounded-full border border-amber-400/60 bg-amber-500/10">
                      <Check className="h-9 w-9 text-amber-300" />
                    </span>
                    <h4 className="mt-8 font-luxe text-2xl uppercase leading-[1.2] tracking-[0.12em] text-neutral-50">
                      Consider it <span className="font-serif normal-case italic text-amber-200">handled.</span>
                    </h4>
                    <p className="mt-4 max-w-xs text-sm leading-relaxed text-neutral-400">
                      Your request is with our concierge desk. Expect a personal reply within hours.
                    </p>
                    <p className="mt-6 border border-amber-500/25 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/80">
                      Reference {confirmedId.slice(0, 8)}
                    </p>
                    <button
                      data-testid="success-close-button"
                      onClick={closeAndReset}
                      className="mt-10 bg-amber-500 px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
                    >
                      Return to the Journey
                    </button>
                  </motion.div>
                ) : (
                  <div className="space-y-8">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
                        Selected experiences — {selectedServices.length}
                      </p>
                      {selectedServices.length === 0 ? (
                        <p data-testid="drawer-empty-list" className="mt-3 border border-dashed border-neutral-700 p-4 text-sm text-neutral-500">
                          Your list is empty. Close this panel and press anything you desire in the catalogue.
                        </p>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedServices.map((s) => (
                            <span
                              key={s.id}
                              className="flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 px-3 py-1.5 text-xs text-amber-100"
                            >
                              {s.name}
                              <button
                                data-testid={`remove-service-${s.id}`}
                                onClick={() => onToggle(s.id)}
                                className="text-neutral-500 transition-colors hover:text-amber-300"
                                aria-label={`Remove ${s.name}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-5">
                      <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
                        <CalendarDays className="h-3.5 w-3.5 text-amber-400/70" /> The Journey
                      </p>
                      <p data-testid="drawer-destination" className="-mt-2 text-xs text-neutral-500">
                        Destination: <span className="text-amber-200/90">{country}</span>
                        <span className="text-neutral-600"> — change it at the top of the catalogue</span>
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Arrival date">
                          <input type="date" data-testid="input-arrival-date" value={form.arrival_date} onChange={set("arrival_date")} className={inputCls} />
                        </Field>
                        <Field label="Departure date">
                          <input type="date" data-testid="input-departure-date" value={form.departure_date} onChange={set("departure_date")} className={inputCls} />
                        </Field>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Arrival flight # (optional)">
                          <div className="relative">
                            <PlaneLanding className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
                            <input data-testid="input-arrival-flight" placeholder="FI 450" value={form.arrival_flight} onChange={set("arrival_flight")} className={`${inputCls} pl-10`} />
                          </div>
                        </Field>
                        <Field label="Departure flight # (optional)">
                          <div className="relative">
                            <PlaneTakeoff className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
                            <input data-testid="input-departure-flight" placeholder="FI 451" value={form.departure_flight} onChange={set("departure_flight")} className={`${inputCls} pl-10`} />
                          </div>
                        </Field>
                      </div>
                      <Field label="Party size">
                        <div className="relative">
                          <Users className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
                          <input type="number" min="1" max="200" data-testid="input-party-size" value={form.party_size} onChange={set("party_size")} className={`${inputCls} pl-10`} />
                        </div>
                      </Field>
                    </div>

                    <div className="space-y-5 border border-amber-500/15 bg-amber-500/[0.03] p-5">
                      <label className="flex cursor-pointer items-center justify-between">
                        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
                          <BedDouble className="h-3.5 w-3.5" /> Already booked something?
                        </span>
                        <input
                          type="checkbox"
                          data-testid="toggle-already-booked"
                          checked={form.already_booked}
                          onChange={set("already_booked")}
                          className="h-4 w-4 accent-amber-500"
                        />
                      </label>
                      {form.already_booked && (
                        <motion.div
                          className="space-y-4"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          transition={{ duration: 0.4 }}
                        >
                          <Field label="Where are you staying?">
                            <input data-testid="input-hotel-name" placeholder="Hotel, villa or address" value={form.hotel_name} onChange={set("hotel_name")} className={inputCls} />
                          </Field>
                          <Field label="Hotel address / area (optional)">
                            <input data-testid="input-hotel-address" placeholder="e.g. Austurstræti, 101 Reykjavík" value={form.hotel_address} onChange={set("hotel_address")} className={inputCls} />
                          </Field>
                          <Field label="Anything else already arranged?">
                            <textarea
                              data-testid="input-existing-bookings"
                              rows={2}
                              placeholder="Tours, dinners or transfers you've booked yourself — we'll plan logistics around them."
                              value={form.existing_bookings}
                              onChange={set("existing_bookings")}
                              className={`${inputCls} resize-none`}
                            />
                          </Field>
                        </motion.div>
                      )}
                    </div>

                    <div className="space-y-5">
                      <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
                        <Plane className="h-3.5 w-3.5 text-amber-400/70" /> About You
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Full name *">
                          <input data-testid="input-name" placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} />
                        </Field>
                        <Field label="Email *">
                          <input type="email" data-testid="input-email" placeholder="you@email.com" value={form.email} onChange={set("email")} className={inputCls} />
                        </Field>
                      </div>
                      <Field label="Phone / WhatsApp (optional)">
                        <input data-testid="input-phone" placeholder="+1 ..." value={form.phone} onChange={set("phone")} className={inputCls} />
                      </Field>
                      <Field label="Special desires">
                        <textarea
                          data-testid="input-message"
                          rows={3}
                          placeholder="Occasions, surprises, dietary notes, must-sees — anything at all."
                          value={form.message}
                          onChange={set("message")}
                          className={`${inputCls} resize-none`}
                        />
                      </Field>
                    </div>
                  </div>
                )}
              </div>

              {!confirmedId && (
                <div className="border-t border-amber-500/15 px-6 py-5">
                  <button
                    data-testid="drawer-submit-request"
                    onClick={submit}
                    disabled={sending}
                    className="flex w-full items-center justify-center gap-3 bg-amber-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {sending ? "Sealing your request..." : "Send to the Concierge"}
                  </button>
                  <p className="mt-3 text-center text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                    No payment now — a human concierge replies first
                  </p>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
